import { cx } from "@/lib/cx";
import { enterProps } from "@/ui/motion";

export type SpinnerProps = {
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Announced to screen readers while work is in flight. */
  label?: string;
};

const SIZES = {
  sm: { box: "size-4", ring: 1.5 },
  md: { box: "size-5", ring: 2 },
  lg: { box: "size-8", ring: 2.5 },
} as const;

/**
 * In-flight work, and only where waiting is unavoidable — a submit that has to
 * reach the server before the screen can move on. It is not the app's loading
 * state: a screen that knows its own shape shows a `Skeleton` instead, and a
 * write that can be rolled back stays optimistic and shows nothing.
 *
 * Drawn in the current text colour at full strength. It used to carry
 * `opacity-60`, which inside a primary button made the spinner fainter than the
 * label it replaced — the one moment where the control most needs to look alive
 * was the moment it looked switched off.
 *
 * **The arc is a fading tail, not a border with one side rubbed out.** The old
 * shape was `border-current border-r-transparent`, which is a hard three-quarter
 * ring with a butt end — the web spinner. The native indicator fades from
 * opaque to nothing around the circle, and a `conic-gradient` through a ring
 * mask is exactly that, in one element and with no keyframes of its own beyond
 * the globally scaled spin cycle.
 *
 * The gradient and the mask are inline `style` rather than arbitrary Tailwind
 * values on purpose: both are comma-heavy functions, and an arbitrary value
 * carrying commas and parentheses is the kind of string that survives a build
 * but silently mangles under a different minifier. A style object cannot be
 * misparsed.
 */
export function Spinner({ size = "md", className, label = "Loading" }: SpinnerProps) {
  const { box, ring } = SIZES[size];
  const hole = `calc(100% - ${ring}px)`;

  return (
    <span
      {...enterProps("status")}
      role="status"
      aria-label={label}
      className={cx("inline-flex shrink-0", className)}
    >
      {/* Keep the arrival animation separate from the rotation: both own
          `animation-name`, so placing them on one element would replace the
          spinner rather than compose with it. */}
      <span
        aria-hidden
        className={cx("busee-motion-spin ps1-round block rounded-full", box)}
        style={{
          background: `conic-gradient(from 0deg, transparent 0deg, currentColor 320deg, currentColor 360deg)`,
          mask: `radial-gradient(farthest-side, transparent ${hole}, #000 0)`,
          WebkitMask: `radial-gradient(farthest-side, transparent ${hole}, #000 0)`,
        }}
      />
    </span>
  );
}
